import { LeftOutlined } from "@ant-design/icons"
import { Button, Flex, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { TEMPLATE_DOCUMENT_TYPES } from "../constants/template-document-type.tsx"
import {
  createTemplate,
  fetchAvailableBlocks,
  previewTemplate
} from "../redux/thunks/document-templates.thunks.ts"
import {
  type DocumentTemplateBlock,
  type DocumentTemplateMutateData
} from "../types/types.ts"
import { AvailableBlocksLoadFailed } from "./components/AvailableBlocksLoadFailed.tsx"
import { DocumentTemplateForm } from "./components/DocumentTemplateForm.tsx"

const { Title, Text } = Typography

function DocumentTemplateAddPage({
  availableBlocks,
  availableBlocksStatus,
  getBlocks,
  createTemplate,
  previewTemplate
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()

  const handleSave = useCallback(
    async (payload: DocumentTemplateMutateData) => {
      try {
        await createTemplate(payload).unwrap()
        navigate(RoutesPaths.settings.documentTemplates)
      } catch (e) {
        console.log(e)
      }
    },
    [createTemplate, navigate]
  )

  const handlePreview = useCallback(
    async (payload: DocumentTemplateMutateData) => {
      return await previewTemplate(payload).unwrap()
    },
    [previewTemplate]
  )

  useEffect(() => {
    getBlocks()
  }, [getBlocks])

  return (
    <FixedContainer>
      <Flex justify={"space-between"}>
        <Space>
          <Flex vertical>
            <Space>
              <Link to={RoutesPaths.settings.documentTemplates}>
                <Button icon={<LeftOutlined />} />
              </Link>
              <Title level={4}>Create Template</Title>
            </Space>
            <Text type="secondary">
              Create your proposal and contract templates
            </Text>
          </Flex>
        </Space>
      </Flex>
      {availableBlocksStatus === "failed" ? (
        <AvailableBlocksLoadFailed onRetry={() => void getBlocks()} />
      ) : availableBlocksStatus !== "succeeded" ? (
        <Skeleton.Node active className={"size-full"} />
      ) : (
        <DocumentTemplateForm
          availableBlocks={availableBlocks}
          onPreview={handlePreview}
          initialValues={{
            name: "",
            doc_type: TEMPLATE_DOCUMENT_TYPES.PROPOSAL_CONTRACT,
            blocks: availableBlocks?.reduce((ar, el, i) => {
              if (!el?.removable || el?.is_default)
                ar.push({
                  id: crypto.randomUUID(),
                  block_type: el?.block_type,
                  order: i,
                  config: el?.default_config
                })

              return ar
            }, [] as DocumentTemplateBlock[])
          }}
          onSubmit={handleSave}
        />
      )}
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  availableBlocks: state.documentTemplates.availableBlocks || [],
  availableBlocksStatus: state.documentTemplates.availableBlocksStatus
})

const mapDispatchToProps = {
  getBlocks: fetchAvailableBlocks,
  createTemplate,
  previewTemplate
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentTemplateAddPage)
